/**
 * Page d'inscription (création de compte)
 *
 * Rôle : Formulaire multi-étapes (identité, société, SIRENE, secteur/métier, plan) ; appelle /api/auth/register.
 * Recherche SIRENE par nom (/api/sirene/search) et lookup par SIRET (/api/sirene/lookup). Données secteur/métier dans sector-metiers.
 * Technologies : React, Next.js, API register + sirene, lib/utils/plans, lib/data/sector-metiers
 */
"use client";

import Image from "next/image";
import Link from "next/link";
import { useMemo, useState, useRef, useEffect } from "react";
import { PLAN_CARDS, calculateProPrice, planIdToApiName, type PlanId } from "@/lib/utils/plans";
import { getMetiersForSector, filterMetiers } from "@/lib/data/sector-metiers";

type SireneSearchCompany = {
  companyName: string;
  siret: string;
  siren: string;
  address: string;
  postalCode: string;
  city: string;
  country: string;
  ape: string;
  vatNumber: string;
  companySize: string;
};

export default function SignUpPage() {
  // Identité compte principal
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName]   = useState("");
  const [email, setEmail]         = useState("");
  const [phone, setPhone]         = useState("");
  const [role, setRole]           = useState("Direction");

  // Société
  const [company, setCompany]     = useState("");
  const [website, setWebsite]     = useState("");
  const [companySize, setCompanySize] = useState("1-5");
  const [ape, setApe]             = useState("");
  const [siret, setSiret]         = useState("");
  const [vat, setVat]             = useState(""); // TVA intracom
  const [country, setCountry]     = useState("France");
  const [address, setAddress]     = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity]           = useState("");
  const [logoFile, setLogoFile]   = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [description, setDescription] = useState("");

  // Facturation (si différent)
  const [billingEmail, setBillingEmail] = useState("");
  const [billingPhone, setBillingPhone] = useState("");

  // Contexte commercial
  const [howHeard, setHowHeard] = useState("Recommandation");
  const [useCase, setUseCase]   = useState("");

  // Sécurité / plan
  const [pwd, setPwd]   = useState("");
  const [pwd2, setPwd2] = useState("");
  const [plan, setPlan] = useState<PlanId>("FREE");
  const [numUsers, setNumUsers] = useState(5);
  const [priorityPositioning, setPriorityPositioning] = useState(false);
  const [sponsorAgreement, setSponsorAgreement] = useState(false);
  const [agree, setAgree] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [sireneLoading, setSireneLoading] = useState(false);
  const [sireneError, setSireneError] = useState<string | null>(null);
  const [sireneSearchResults, setSireneSearchResults] = useState<SireneSearchCompany[] | null>(null);
  const sireneDropdownRef = useRef<HTMLDivElement>(null);
  const [metier, setMetier] = useState("");
  const [metierSearchFilter, setMetierSearchFilter] = useState("");
  const [metierDropdownOpen, setMetierDropdownOpen] = useState(false);
  const metierDropdownRef = useRef<HTMLDivElement>(null);

  // Gestion des étapes
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;
  
  // Gestion du popup de développement
  const [showDevPopup, setShowDevPopup] = useState(true);
  
  const sectorOptions = [
    "Marine",
    "Société de services",
    "Construction",
    "Commerce",
    "Alimentation",
    "Hôtellerie",
    "Industrie"
  ];

  const metiersForSector = useMemo(() => getMetiersForSector(ape), [ape]);
  const filteredMetiers = useMemo(
    () => filterMetiers(metiersForSector, metierSearchFilter),
    [metiersForSector, metierSearchFilter]
  );

  const passwordPolicyRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z0-9]).{9,}$/;
  const isPasswordStrong = useMemo(() => passwordPolicyRegex.test(pwd), [pwd]);

  // Validation par étape
  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1: // Informations personnelles
        return firstName.trim() !== "" && lastName.trim() !== "" && email.trim() !== "" && phone.trim() !== "";
      case 2: // Société
        return company.trim() !== "" && address.trim() !== "" && city.trim() !== "" && postalCode.trim() !== "" && ape.trim() !== "";
      case 3: // Plan et sécurité
        return isPasswordStrong && pwd === pwd2 && agree;
      case 4: // Finalisation
        return true;
      default:
        return false;
    }
  };
  
  // Calcul du pourcentage de completion
  const completionPercentage = useMemo(() => {
    let completedFields = 0;
    const totalFields = 15; // Nombre total de champs importants
    
    if (firstName.trim()) completedFields++;
    if (lastName.trim()) completedFields++;
    if (email.trim()) completedFields++;
    if (phone.trim()) completedFields++;
    if (company.trim()) completedFields++;
    if (website.trim()) completedFields++;
    if (address.trim()) completedFields++;
    if (city.trim()) completedFields++;
    if (postalCode.trim()) completedFields++;
    if (ape.trim()) completedFields++;
    if (siret.trim()) completedFields++;
    if (isPasswordStrong) completedFields++;
    if (pwd === pwd2 && pwd.length > 0) completedFields++;
    if (agree) completedFields++;
    if (plan !== "FREE") completedFields++;
    
    return Math.round((completedFields / totalFields) * 100);
  }, [firstName, lastName, email, phone, company, website, address, city, postalCode, ape, siret, pwd, pwd2, agree, plan, isPasswordStrong]);
  
  // Navigation entre étapes
  const nextStep = () => {
    if (currentStep < totalSteps && validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
      setError(null);
    }
  };
  
  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError(null);
    }
  };

  // Score mot de passe
  const pwdScore = useMemo(() => {
    let s = 0;
    if (pwd.length >= 9) s++;
    if (pwd.match(/[a-z]/)) s++;
    if (pwd.match(/[A-Z]/)) s++;
    if (pwd.match(/[0-9]/)) s++;
    if (pwd.match(/[^a-zA-Z0-9]/)) s++;
    return s;
  }, [pwd]);

  const pwdLabel = useMemo(() => {
    if (pwdScore <= 2) return "Faible";
    if (pwdScore === 3) return "Moyen";
    if (pwdScore === 4) return "Sécurisé";
    return "Très sécurisé";
  }, [pwdScore]);

  const canSubmit = useMemo(() => {
    const basicValidation = (
      firstName.trim() !== "" &&
      lastName.trim() !== "" &&
      email.trim() !== "" &&
      phone.trim() !== "" &&
      company.trim() !== "" &&
      address.trim() !== "" &&
      city.trim() !== "" &&
      postalCode.trim() !== "" &&
      ape.trim() !== "" &&
      isPasswordStrong &&
      pwd === pwd2 &&
      agree
    );
    
    // Si l'offre Publicité est sélectionnée, l'accord sponsor est requis
    if (plan === "PREMIUM") {
      return basicValidation && sponsorAgreement;
    }
    
    return basicValidation;
  }, [firstName, lastName, email, phone, company, address, city, postalCode, pwd, pwd2, agree, plan, sponsorAgreement, isPasswordStrong]);

  const fillFormFromCompany = (companyData: SireneSearchCompany) => {
    if (companyData.companyName) setCompany(companyData.companyName);
    if (companyData.address) setAddress(companyData.address);
    if (companyData.postalCode) setPostalCode(companyData.postalCode);
    if (companyData.city) setCity(companyData.city);
    if (companyData.country) setCountry(companyData.country);
    if (companyData.vatNumber) setVat(companyData.vatNumber);
    if (companyData.companySize) setCompanySize(companyData.companySize);
    if (companyData.siret && companyData.siret.length === 14) setSiret(companyData.siret);
    if (companyData.ape && sectorOptions.includes(companyData.ape)) {
      setApe(companyData.ape);
    }
  };

  const handleSireneSearchByName = async () => {
    const name = company.trim();
    if (!name || name.length < 2) {
      setSireneError('Saisissez au moins 2 caractères (nom de l\'entreprise) pour rechercher.');
      setSireneSearchResults(null);
      return;
    }

    setSireneLoading(true);
    setSireneError(null);
    setSireneSearchResults(null);

    try {
      const response = await fetch(`/api/sirene/search?q=${encodeURIComponent(name)}`);
      const data = await response.json();

      if (!response.ok) {
        setSireneError(data?.error || 'Aucune société trouvée');
        return;
      }

      const companies = data.companies || [];
      if (companies.length === 0) {
        setSireneError('Aucune entreprise trouvée pour ce nom.');
        return;
      }
      setSireneSearchResults(companies);
    } catch (err) {
      setSireneError('Erreur lors de la recherche Sirene');
    } finally {
      setSireneLoading(false);
    }
  };

  const handleSelectSireneCompany = (item: SireneSearchCompany) => {
    fillFormFromCompany(item);
    setSireneSearchResults(null);
    setSireneError(null);
  };

  const handleSireneLookupBySiret = async () => {
    const lookupValue = siret.replace(/\s/g, '');
    if (!lookupValue) return;

    setSireneLoading(true);
    setSireneError(null);
    setSireneSearchResults(null);

    try {
      const params = /^\d{14}$/.test(lookupValue)
        ? `siret=${lookupValue}`
        : `siren=${lookupValue}`;

      const response = await fetch(`/api/sirene/lookup?${params}`);
      const data = await response.json();

      if (!response.ok || !data?.success) {
        setSireneError(data?.error || 'Aucune société trouvée');
        return;
      }

      const companyData = data.company || {};
      fillFormFromCompany(companyData as SireneSearchCompany);
    } catch (err) {
      setSireneError('Erreur lors de la récupération Sirene');
    } finally {
      setSireneLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (sireneDropdownRef.current && !sireneDropdownRef.current.contains(e.target as Node)) {
        setSireneSearchResults(null);
      }
      if (metierDropdownRef.current && !metierDropdownRef.current.contains(e.target as Node)) {
        setMetierDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
      setLoading(true);
      setError(null);

    try {
      // Étape 1 : Créer le compte
      const siretNormalized = siret.replace(/\s/g, '');
      const sirenValue = siretNormalized.length === 9 ? siretNormalized : siretNormalized.substring(0, 9);
      const siretValue = siretNormalized.length === 14 ? siretNormalized : '';

      const registerResponse = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          // Informations utilisateur
          email,
          password: pwd,
          firstName,
          lastName,
          phone,
          position: role,
          howHeard,
          useCase: useCase || undefined,
          
          // Secteur et métier (pour la marketplace)
          industry: ape || undefined,
          metier: metier || undefined,
          
          // Informations entreprise
          companyName: company,
          siren: sirenValue || undefined,
          siret: siretValue,
          vatNumber: vat,
          address,
          postalCode,
          city,
          country,
          companyPhone: phone,
          website,
          companySize,
          description: description.trim() || undefined,
          
          // Plan et facturation
          plan: planIdToApiName(plan),
          billingEmail: billingEmail || email,
          billingPhone: billingPhone || phone,
          numUsers: plan === "STANDARD" ? numUsers : undefined,
          priorityPositioning: plan === "STANDARD" ? priorityPositioning : undefined,
        }),
      });

      const responseText = await registerResponse.text();
      let registerData: {
        success?: boolean;
        error?: string;
        message?: string;
        company?: { id?: string | number };
      } | null = null;
      if (responseText) {
        try {
          registerData = JSON.parse(responseText) as {
            success?: boolean;
            error?: string;
            message?: string;
            company?: { id?: string | number };
          };
        } catch (parseError) {
          console.warn("Réponse JSON invalide de /api/auth/register:", parseError);
        }
      }

      if (!registerResponse.ok) {
        throw new Error(
          registerData?.error ||
            registerData?.message ||
            "Erreur lors de la création du compte"
        );
      }
      if (!registerData) {
        throw new Error("Réponse serveur invalide lors de la création du compte");
      }
      
      // Étape 2 : Uploader le logo si fourni
      if (logoFile && registerData.success && registerData.company?.id) {
        try {
          const logoFormData = new FormData();
          logoFormData.append('logo', logoFile);

          const logoResponse = await fetch("/api/companies/logo/upload", {
            method: "POST",
            body: logoFormData,
          });

          if (!logoResponse.ok) {
            console.warn('Erreur lors de l\'upload du logo, mais le compte a été créé');
          }
        } catch (logoError) {
          console.warn('Erreur lors de l\'upload du logo:', logoError);
          // Ne pas bloquer l'inscription si l'upload du logo échoue
        }
      }
      
      // Redirection vers la page de connexion avec un message de succès
      if (registerData.success) {
        window.location.href = "/signin?message=Compte créé avec succès";
      } else {
        throw new Error(registerData.error || registerData.message || "Erreur lors de la création du compte");
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Une erreur est survenue.";
      setError(errorMessage);
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Conteneur très aéré */}
      <div className="px-6 py-0">
        <div className="mx-auto w-full max-w-6xl">
          {/* Carte formulaire bien arrondie */}
          <div className="rounded-3xl border border-gray-200 bg-white shadow-lg p-6 sm:p-8 lg:p-10">
            <div className="mb-8 text-center">
              <Link href="/">
                <Image
                  src="/logos/otulaa-logo.png"
                  alt="Otulaa"
                  width={200}
                  height={50}
                  priority
                  className="h-auto mx-auto mb-6 hover:opacity-80 transition-opacity duration-200"
                />
              </Link>
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">Créer un compte</h1>
              <p className="mt-2 text-gray-600">
                Renseignez votre société, vos coordonnées et choisissez votre plan.
              </p>
              
              {/* Barre de progression */}
              <div className="mt-6 mx-auto max-w-md">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Étape {currentStep} sur {totalSteps}</span>
                  <span className="text-sm font-medium text-orange-600">{completionPercentage}% complété</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-orange-400 to-red-500 h-2 rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                  ></div>
                </div>
                <div className="flex justify-between mt-2">
                  <span className="text-xs text-gray-500">Informations personnelles</span>
                  <span className="text-xs text-gray-500">Société</span>
                  <span className="text-xs text-gray-500">Sécurité</span>
                  <span className="text-xs text-gray-500">Finalisation</span>
                </div>
              </div>
              
            </div>

            <form className="space-y-8" onSubmit={onSubmit}>
              {/* Étape 1: Informations personnelles */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">Informations personnelles</h2>
                    <p className="text-gray-600 mt-2">Renseignez vos coordonnées principales</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-sm font-semibold text-gray-900">Prénom *</label>
                      <Input
                        value={firstName}
                        onChange={setFirstName}
                        placeholder="Votre prénom"
                        required
                        autoComplete="given-name"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-900">Nom *</label>
                      <Input
                        value={lastName}
                        onChange={setLastName}
                        placeholder="Votre nom"
                        required
                        autoComplete="family-name"
                      />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-sm font-semibold text-gray-900">Email *</label>
                      <Input
                        type="email"
                        value={email}
                        onChange={setEmail}
                        placeholder="votre@email.com"
                        required
                        autoComplete="email"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-900">Téléphone *</label>
                      <Input
                        type="tel"
                        value={phone}
                        onChange={setPhone}
                        placeholder="06 12 34 56 78"
                        required
                        autoComplete="tel"
                      />
                </div>
                </div>
                  
                  <div>
                    <label className="text-sm font-semibold text-gray-900">Rôle dans l&apos;entreprise</label>
                    <Select
                      value={role}
                      onChange={setRole}
                      options={["Direction", "Gestion", "Commercial", "Technique", "Autre"]}
                    />
                  </div>
                </div>
              )}
              
              {/* Étape 2: Société */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">Votre société</h2>
                    <p className="text-gray-600 mt-2">Informations sur votre entreprise</p>
                  </div>

                  {/* Auto-remplir SIRENE en haut : nom + bouton (style header) */}
                  <div ref={sireneDropdownRef} className="relative">
                    <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
                      <div className="flex-1">
                        <label className="text-sm font-semibold text-gray-900">Raison sociale *</label>
                        <Input
                          value={company}
                          onChange={(v) => {
                            setCompany(v);
                            setSireneError(null);
                            setSireneSearchResults(null);
                          }}
                          placeholder="Nom de votre entreprise"
                          required
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleSireneSearchByName}
                        disabled={sireneLoading || !company.trim()}
                        className="inline-flex justify-center items-center gap-2 rounded-xl bg-gradient-to-r from-orange-400 to-red-500 text-white px-4 py-2.5 text-sm font-bold shadow-sm transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-md active:scale-95 disabled:opacity-50 disabled:hover:translate-y-0"
                      >
                        {sireneLoading ? (
                          <>
                            <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Recherche...
                          </>
                        ) : (
                          "Auto-remplir"
                        )}
                      </button>
                    </div>
                    {sireneError && (
                      <p className="mt-2 text-sm text-red-600">{sireneError}</p>
                    )}
                    {/* Liste déroulante des entreprises trouvées */}
                    {sireneSearchResults && sireneSearchResults.length > 0 && (
                      <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-72 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg">
                        <p className="px-4 py-2 text-xs font-semibold text-gray-500 border-b bg-gray-50">
                          Choisissez votre entreprise (adresse, ville)
                        </p>
                        {sireneSearchResults.map((item) => (
                          <button
                            key={item.siret}
                            type="button"
                            onClick={() => handleSelectSireneCompany(item)}
                            className="w-full text-left px-4 py-3 hover:bg-orange-50 border-b border-gray-100 last:border-b-0 transition-colors"
                          >
                            <span className="font-semibold text-gray-900 block">{item.companyName}</span>
                            <span className="text-sm text-gray-600">
                              {[item.address, item.postalCode, item.city].filter(Boolean).join(", ")}
                            </span>
                            {item.siret && (
                              <span className="text-xs text-gray-400 block mt-0.5">SIRET {item.siret}</span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  
                <div>
                    <label className="text-sm font-semibold text-gray-900">Site web</label>
                    <Input
                      value={website}
                      onChange={setWebsite}
                      placeholder="https://votre-site.com"
                    />
                  </div>

                  {/* Upload du logo */}
                  <div>
                    <label className="text-sm font-semibold text-gray-900">Logo de l&apos;entreprise</label>
                    <div className="mt-2">
                      {logoPreview ? (
                        <div className="flex items-center gap-4">
                          <img
                            src={logoPreview}
                            alt="Aperçu du logo"
                            className="w-24 h-24 object-contain border border-gray-300 rounded-lg p-2 bg-white"
                          />
                          <div className="flex-1">
                            <p className="text-sm text-gray-600 mb-2">{logoFile?.name}</p>
                            <button
                              type="button"
                              onClick={() => {
                                setLogoFile(null);
                                setLogoPreview(null);
                              }}
                              className="text-sm text-red-600 hover:text-red-700"
                            >
                              Supprimer
                            </button>
                          </div>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <svg className="w-10 h-10 mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            <p className="mb-2 text-sm text-gray-500">
                              <span className="font-semibold">Cliquez pour uploader</span> ou glissez-déposez
                            </p>
                            <p className="text-xs text-gray-500">PNG, JPG, GIF ou WebP (MAX. 5MB)</p>
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                // Vérifier la taille
                                if (file.size > 5 * 1024 * 1024) {
                                  alert('Le fichier est trop volumineux. Taille maximale : 5MB');
                                  return;
                                }
                                setLogoFile(file);
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  setLogoPreview(reader.result as string);
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="text-sm font-semibold text-gray-900">Taille de l&apos;entreprise</label>
                      <Select
                        value={companySize}
                        onChange={setCompanySize}
                        options={["1-5", "6-20", "21-50", "51-200", "200+"]}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-900">Secteur d&apos;activité *</label>
                      <Select
                        value={ape}
                        onChange={(v) => {
                          setApe(v);
                          setMetier("");
                          setMetierSearchFilter("");
                          setMetierDropdownOpen(false);
                        }}
                        options={sectorOptions}
                        placeholder="Choisir un secteur"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-900">SIREN / SIRET</label>
                      <div className="flex gap-2">
                        <Input
                          value={siret}
                          onChange={(value) => {
                            setSiret(value);
                            setSireneError(null);
                          }}
                          placeholder="SIREN (9) ou SIRET (14)"
                        />
                        <button
                          type="button"
                          onClick={handleSireneLookupBySiret}
                          disabled={sireneLoading || !siret.trim()}
                          className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-orange-400 to-red-500 text-white px-4 py-2 text-sm font-bold shadow-sm transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-md active:scale-95 disabled:opacity-50 disabled:hover:translate-y-0"
                        >
                          {sireneLoading ? "..." : "Remplir"}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Métier / Univers : liste déroulante avec recherche (affichée après choix du secteur) */}
                  {ape && (
                    <div ref={metierDropdownRef} className="relative">
                      <label className="text-sm font-semibold text-gray-900">Métier / Univers</label>
                      <div className="mt-1">
                        <div className="flex rounded-lg border border-gray-300 bg-white shadow-sm overflow-hidden">
                          <input
                            type="text"
                            value={metierDropdownOpen ? metierSearchFilter : metier}
                            onChange={(e) => {
                              setMetierSearchFilter(e.target.value);
                              setMetierDropdownOpen(true);
                            }}
                            onFocus={() => setMetierDropdownOpen(true)}
                            placeholder="Tapez pour rechercher (ex. Plo → Plombier)..."
                            className="flex-1 min-w-0 px-3 py-2 text-sm border-0 focus:ring-2 focus:ring-orange-400 focus:ring-inset outline-none"
                          />
                          <button
                            type="button"
                            onClick={() => setMetierDropdownOpen((o) => !o)}
                            className="px-3 py-2 border-l border-gray-200 text-gray-500 hover:bg-gray-50"
                            aria-label="Ouvrir la liste"
                          >
                            <svg className={`w-4 h-4 transition-transform ${metierDropdownOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        </div>
                        {metierDropdownOpen && (
                          <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                            {filteredMetiers.length === 0 ? (
                              <p className="px-4 py-3 text-sm text-gray-500">Aucun métier trouvé pour &quot;{metierSearchFilter}&quot;</p>
                            ) : (
                              filteredMetiers.map((m) => (
                                <button
                                  key={m}
                                  type="button"
                                  onClick={() => {
                                    setMetier(m);
                                    setMetierSearchFilter("");
                                    setMetierDropdownOpen(false);
                                  }}
                                  className={`w-full text-left px-4 py-2.5 text-sm hover:bg-orange-50 border-b border-gray-100 last:border-b-0 transition-colors ${metier === m ? "bg-orange-50 font-medium text-orange-700" : "text-gray-900"}`}
                                >
                                  {m}
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <label className="text-sm font-semibold text-gray-900">Adresse *</label>
                    <Input
                      value={address}
                      onChange={setAddress}
                      placeholder="123 Rue de la Paix"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="text-sm font-semibold text-gray-900">Code postal *</label>
                      <Input
                        value={postalCode}
                        onChange={setPostalCode}
                        placeholder="75001"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-900">Ville *</label>
                      <Input
                        value={city}
                        onChange={setCity}
                        placeholder="Paris"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-900">Pays</label>
                      <Select
                        value={country}
                        onChange={setCountry}
                        options={["France", "Belgique", "Suisse", "Canada", "Autre"]}
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Étape 3: Plan et sécurité */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">Plan et sécurité</h2>
                    <p className="text-gray-600 mt-2">Choisissez votre plan et sécurisez votre compte</p>
                  </div>

              {/* Section Plan - mêmes cartes que account/informations */}
                  <div>
                    <label className="text-sm font-semibold text-gray-900 mb-4 block">Choisissez votre plan</label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {PLAN_CARDS.map((p) => (
                        <PlanCard
                          key={p.id}
                          id={p.id}
                          title={p.title}
                          price={p.price}
                          badge={p.badge}
                          badgeClassName={p.badgeClassName}
                          features={p.features}
                          checked={plan === p.id}
                          onSelect={setPlan}
                          priorityPositioning={priorityPositioning}
                          setPriorityPositioning={setPriorityPositioning}
                          sponsorAgreement={sponsorAgreement}
                          setSponsorAgreement={setSponsorAgreement}
                          numUsers={numUsers}
                          setNumUsers={setNumUsers}
                        />
                      ))}
                    </div>
                  </div>
                  
                  {/* Section Mot de passe */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-sm font-semibold text-gray-900">Mot de passe *</label>
                      <Input
                        type="password"
                        value={pwd}
                        onChange={setPwd}
                        placeholder="••••••••"
                        required
                        minLength={9}
                        autoComplete="new-password"
                      />
                      {pwd && !isPasswordStrong && (
                        <p className="text-xs text-red-600 mt-1">
                          Minimum 9 caractères, 1 majuscule, 1 minuscule et 1 signe.
                        </p>
                      )}
                      {pwd && (
                        <div className="mt-2">
                          <div className="flex gap-1">
                            {[1, 2, 3, 4].map((i) => (
                              <div
                                key={i}
                                className={`h-1 flex-1 rounded ${
                                  i <= pwdScore ? "bg-green-500" : "bg-gray-200"
                                }`}
                              />
                            ))}
                          </div>
                          <p className="text-xs text-gray-600 mt-1">
                            {pwdScore === 4 ? "Très sécurisé" : 
                             pwdScore === 3 ? "Sécurisé" : 
                             pwdScore === 2 ? "Moyen" : "Faible"}
                          </p>
                        </div>
                      )}
                      </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-900">Confirmer le mot de passe *</label>
                      <Input
                        type="password"
                        value={pwd2}
                        onChange={setPwd2}
                        placeholder="••••••••"
                        required
                        autoComplete="new-password"
                      />
                      {pwd2 && pwd !== pwd2 && (
                        <p className="text-xs text-red-600 mt-1">Les mots de passe ne correspondent pas</p>
                      )}
                    </div>
                </div>

                  <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={agree}
                    onChange={(e) => setAgree(e.target.checked)}
                      className="rounded border-gray-300 mt-1"
                    required
                  />
                    <label className="text-sm text-gray-700">
                      J&apos;accepte les{" "}
                      <a href="#" className="text-orange-600 hover:underline">Conditions Générales</a>.
                </label>
                  </div>
                </div>
              )}
              
              {/* Étape 4: Finalisation */}
              {currentStep === 4 && (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">Récapitulatif</h2>
                    <p className="text-gray-600 mt-2">Vérifiez vos informations avant de finaliser</p>
                  </div>
                  
                  <div className="bg-gray-50 rounded-2xl p-6 space-y-4">
                    <div>
                      <h3 className="font-semibold text-gray-900">Informations personnelles</h3>
                      <p className="text-gray-600">{firstName} {lastName}</p>
                      <p className="text-gray-600">{email}</p>
                      <p className="text-gray-600">{phone}</p>
                    </div>
                    
                    <div>
                      <h3 className="font-semibold text-gray-900">Société</h3>
                      <p className="text-gray-600">{company}</p>
                      <p className="text-gray-600">{address}, {postalCode} {city}</p>
                    </div>
                    
                    <div>
                      <h3 className="font-semibold text-gray-900">Plan choisi</h3>
                      <p className="text-gray-600">
                        {plan === "FREE" ? "Découverte (0 €/mois)" : 
                         plan === "STANDARD" ? `Pro (${calculateProPrice(numUsers) + (priorityPositioning ? 50 : 0)} €/mois${numUsers > 5 ? ` pour ${numUsers} utilisateurs` : " pour 5 utilisateurs"})` : 
                         "Offre Publicité (200 €/mois)"}
                      </p>
                    </div>
                  </div>
                  </div>
                )}

              {/* Navigation entre étapes */}
              <div className="flex justify-between items-center pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={prevStep}
                  disabled={currentStep === 1}
                  className="px-6 py-3 text-gray-600 font-semibold rounded-2xl border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                >
                  ← Précédent
                </button>

                {currentStep < totalSteps ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    disabled={!validateStep(currentStep)}
                    className="px-8 py-3 bg-gradient-to-r from-orange-400 to-red-500 text-white font-bold rounded-2xl shadow-lg transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-xl active:scale-95 disabled:opacity-50 disabled:hover:translate-y-0"
                  >
                    Suivant →
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={!canSubmit || loading}
                    className="px-8 py-3 bg-gradient-to-r from-orange-400 to-red-500 text-white font-bold rounded-2xl shadow-lg transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-xl active:scale-95 disabled:opacity-50 disabled:hover:translate-y-0"
                  >
                    {loading ? "Création du compte…" : "Créer mon compte"}
                  </button>
                )}
              </div>
              
              {error && (
                <div className="rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">
                  {error}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
      
      {/* Popup rétractable de développement */}
      {showDevPopup && (
        <div className="fixed bottom-4 right-4 z-50 max-w-sm">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-orange-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-orange-800">Application en développement</h3>
                <p className="text-sm text-orange-700 mt-1">
                  Otulaa est actuellement en cours de développement. Certaines fonctionnalités peuvent être désactivées ou vous pourriez rencontrer des bugs. Merci pour votre patience !
                </p>
              </div>
              <button
                onClick={() => setShowDevPopup(false)}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Petits composants UI pour un code plus propre ---------- */

function Input({
  type = "text",
  value,
  onChange,
  placeholder,
  required = false,
  minLength,
  autoComplete,
  className = ""
}: {
  type?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  minLength?: number;
  autoComplete?: string;
  className?: string;
}) {
  const baseClassName = "w-full rounded-2xl border border-gray-300 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all duration-300";
  const finalClassName = className ? `${baseClassName} ${className}` : baseClassName;
  
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required={required}
      minLength={minLength}
      autoComplete={autoComplete}
      className={finalClassName}
      suppressHydrationWarning
    />
  );
}

function Select({
  value,
  onChange,
  options,
  placeholder,
  required
}: {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-2xl border border-gray-300 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all duration-300"
      suppressHydrationWarning
      required={required}
    >
      {placeholder ? (
        <option value="" disabled>
          {placeholder}
        </option>
      ) : null}
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
}

type PlanCardFeature = { name: string; subtitle?: string };

function PlanCard({
  id,
  title,
  price,
  badge,
  badgeClassName,
  features,
  checked,
  onSelect,
  priorityPositioning,
  setPriorityPositioning,
  sponsorAgreement,
  setSponsorAgreement,
  numUsers,
  setNumUsers
}: {
  id: PlanId;
  title: string;
  price: string;
  badge?: string;
  badgeClassName?: string;
  features: PlanCardFeature[];
  checked: boolean;
  onSelect: (id: PlanId) => void;
  priorityPositioning?: boolean;
  setPriorityPositioning?: (value: boolean) => void;
  sponsorAgreement?: boolean;
  setSponsorAgreement?: (value: boolean) => void;
  numUsers?: number;
  setNumUsers?: (value: number) => void;
}) {
  const isStandard = id === "STANDARD";
  const isPremium = id === "PREMIUM";
  const standardPrice = isStandard && numUsers !== undefined
    ? calculateProPrice(numUsers) + (priorityPositioning ? 50 : 0)
    : 0;

  return (
    <div
      className={`relative rounded-2xl border-2 p-6 cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-xl ${
        isStandard
          ? checked
            ? "border-orange-500 bg-gradient-to-r from-orange-400 to-red-500 shadow-2xl"
            : "border-orange-400 bg-gradient-to-r from-orange-400 to-red-500 shadow-lg"
          : checked
            ? "border-orange-500 bg-orange-50"
            : "border-gray-200 hover:border-gray-300 bg-white"
      }`}
      onClick={() => onSelect(id)}
    >
      {badge && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
          <span className={`${badgeClassName ?? ""} px-4 py-1 rounded-full text-xs font-bold shadow-lg whitespace-nowrap`}>
            {badge}
          </span>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <h3 className={`font-bold text-lg ${isStandard ? "text-white" : "text-gray-900"}`}>
          {title}
        </h3>
        <div className={`text-3xl font-extrabold ${isStandard ? "text-white" : "text-orange-600"}`}>
          {isStandard && numUsers !== undefined ? `${standardPrice} €` : price}
        </div>
      </div>

      <ul className="space-y-3 text-sm">
        {features.map((f, index) => (
          <li key={index} className="flex items-start gap-3">
            <svg className="w-5 h-5 mt-0.5 flex-shrink-0 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <div className={isStandard ? "text-white font-medium" : "text-gray-600"}>
              <span>{f.name}</span>
              {f.subtitle && <span className="block text-xs opacity-75">{f.subtitle}</span>}
            </div>
          </li>
        ))}
      </ul>

      {isStandard && numUsers !== undefined && setNumUsers && (
        <div className="mt-4 pt-4 border-t border-orange-300">
          <label className={`block text-sm font-semibold mb-2 ${isStandard ? "text-white" : "text-gray-900"}`}>
            Nombre d&apos;utilisateurs
          </label>
          <div className="flex items-center gap-3">
            <input
              type="number"
              min={1}
              value={numUsers}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10) || 1;
                setNumUsers(Math.max(1, val));
              }}
              onClick={(e) => e.stopPropagation()}
              className={`w-24 rounded-lg border-2 px-3 py-2 text-sm font-bold ${
                isStandard ? "border-orange-300 bg-white text-gray-900" : "border-gray-300 bg-white"
              } focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
            />
            <span className={`text-sm ${isStandard ? "text-orange-100" : "text-gray-600"}`}>
              {numUsers === 1 ? "utilisateur" : "utilisateurs"}
            </span>
          </div>
          <p className={`text-xs mt-2 ${isStandard ? "text-orange-100" : "text-gray-600"}`}>
            {numUsers <= 5
              ? "Inclus dans le prix de base (100€)"
              : `Base : 100€ pour 5 utilisateurs + ${(numUsers - 5) * 20}€ pour ${numUsers - 5} utilisateur${numUsers - 5 > 1 ? "s" : ""} supplémentaire${numUsers - 5 > 1 ? "s" : ""} (20€/utilisateur)`}
          </p>
        </div>
      )}

      {isStandard && (
        <div className="mt-4 pt-4 border-t border-orange-300">
          <div className="flex items-center justify-between">
            <div>
              <h4 className={`text-sm font-semibold ${isStandard ? "text-white" : "text-gray-900"}`}>
                Positionnement prioritaire dans la marketplace
              </h4>
              <p className={`text-xs ${isStandard ? "text-orange-100" : "text-gray-600"}`}>
                Vos produits apparaîtront en premier dans les résultats de recherche
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-bold ${isStandard ? "text-orange-200" : "text-orange-600"}`}>
                +50€
              </span>
              <label className="flex items-center" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={priorityPositioning ?? false}
                  onChange={(e) => setPriorityPositioning?.(e.target.checked)}
                  className={`w-4 h-4 ${isStandard ? "text-orange-200" : "text-orange-600"} border-gray-300 rounded focus:ring-orange-500`}
                />
              </label>
            </div>
          </div>
        </div>
      )}

      {isPremium && checked && (
        <div className="mt-4 pt-4 border-t border-gray-300">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-amber-800 mb-2">
                  Attention - Offre réservée aux sponsors
                </h4>
                <p className="text-sm text-amber-700 mb-3">
                  Cette offre est exclusivement destinée aux entreprises souhaitant faire de la publicité sur Otulaa.
                  Vous n&apos;aurez <strong>pas accès</strong> aux fonctionnalités suivantes :
                </p>
                <ul className="text-sm text-amber-700 mb-3 ml-4 space-y-1">
                  <li>• Logiciel de gestion</li>
                  <li>• Site internet e-commerce</li>
                  <li>• Vente sur le marketplace</li>
                  <li>• Plateforme de transport intégrée</li>
                </ul>
                <div className="flex items-start gap-2" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={sponsorAgreement ?? false}
                    onChange={(e) => setSponsorAgreement?.(e.target.checked)}
                    className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500 mt-0.5"
                  />
                  <label className="text-sm text-amber-800 font-medium">
                    Je confirme avoir lu et accepté ces conditions
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {(isStandard || isPremium) && (
        <div className={`mt-4 pt-4 border-t ${isStandard ? "border-orange-300" : "border-gray-300"}`}>
          <p className={`text-xs text-center font-medium ${isStandard ? "text-orange-100" : "text-gray-600"}`}>
            <svg className="w-4 h-4 inline mr-1 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            {isStandard ? "Solution complète pour votre entreprise" : "Solution publicitaire complète"}
          </p>
        </div>
      )}
    </div>
  );
}